import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Trophy, Plus, Trash2, AlertCircle, Users, MessageCircle, Pencil, Clipboard, UserPlus, UserMinus, FileDown } from "lucide-react";
import { OpponentAthleteModal } from "@/components/events/OpponentAthleteModal";
import { deleteEvent } from "@/lib/api/events.functions";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FeeChargeWhatsappModal } from "@/components/fees/FeeChargeWhatsappModal";
import { WalkInAthleteDialog } from "@/components/events/WalkInAthleteDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  listEventsWithCategories, listStudentsByCategory, saveSummon,
  listSummonsByEvent, deleteSummon, unsummonStudent,
  listFees, upsertFee, deleteFee, linkFeeToEvent,
  listEventPaymentStatus, checkSummonConflicts, listStudentSummonActivity,
  createFeeChargesForStudents,
} from "@/lib/api/summons.functions";
import { PAYMENT_METHODS, paymentMethodLabel } from "@/lib/payment-methods";
import { updateChargePaymentMethod } from "@/lib/api/charges.functions";
import { brToday } from "@/lib/tz";
import { buildSummonPdf, DEFAULT_SUMMON_NOTES } from "@/lib/pdf/summon-pdf";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/m/convocacao-jogos")({
  component: ConvocacaoPage,
});

/** Converte um caminho do bucket privado em URL assinada (ou devolve a URL já pronta). */
async function toMediaUrl(value?: string | null): Promise<string | null> {
  if (!value) return null;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  const { data } = await supabase.storage
    .from("training_center_media")
    .createSignedUrl(value, 60 * 60);
  return data?.signedUrl ?? null;
}

type FeeType = "campeonato" | "arbitragem" | "material";


/** "Sub-9" -> 9. Retorna null quando não for categoria Sub-XX. */
function subNum(cat?: string | null): number | null {
  if (!cat) return null;
  const m = /(\d+)/.exec(cat);
  return m ? Number(m[1]) : null;
}

/** Endereço do evento, montado a partir do cadastro feito na criação do evento. */
export function eventAddress(ev: any): string | null {
  if (!ev) return null;
  const parts = [
    [ev.street, ev.number].filter(Boolean).join(", "),
    ev.neighborhood,
    ev.city,
  ].filter((v) => v && String(v).trim());
  const addr = parts.join(" - ");
  return addr || ev.location || null;
}

/** Dados do jogo usados na mensagem de WhatsApp da convocação. */
function summonEventInfo(s: any, ev?: any) {
  return {
    address: s?.location || eventAddress(ev),
    date: s?.match_date ? new Date(s.match_date + "T00:00:00").toLocaleDateString("pt-BR") : null,
    time: s?.match_time ? String(s.match_time).slice(0, 5) : null,
    category: s?.event_categories?.category ?? null,
    field: s?.event_categories?.field_name ?? null,
  };
}

function computeEventLockStatus(e: any): { locked: boolean; reason: "encerrado" | "cancelado" | null } {

  if (!e) return { locked: false, reason: null };
  const status = String(e.status ?? "").toLowerCase();
  if (status.includes("cancel")) return { locked: true, reason: "cancelado" };
  const today = brToday();
  const end = e.end_date ?? e.start_date ?? null;
  if (end && String(end).slice(0, 10) < today) return { locked: true, reason: "encerrado" };
  return { locked: false, reason: null };
}

function EventPickerGrid({ events, onSelect, onChanged }: { events: any[]; onSelect: (id: string) => void; onChanged: () => void }) {
  const [statusFilter, setStatusFilter] = useState<"todos" | "disponivel" | "encerrado" | "cancelado">("disponivel");
  const navigate = useNavigate();
  const delEv = useServerFn(deleteEvent);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Excluir o evento "${name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await delEv({ data: { id } });
      toast.success("Evento excluído");
      onChanged();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao excluir");
    }
  }

  const catLabel = (c: string) => c === "campeonato_interno" ? "Interno"
    : c === "jogos_internos" ? "Jogos Internos" : c === "campeonato_externo" ? "Externo" : "Amistoso";

  const filtered = events.filter((e) => {
    const st = computeEventLockStatus(e);
    if (statusFilter === "todos") return true;
    if (statusFilter === "disponivel") return !st.locked;
    return st.reason === statusFilter;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Selecione o Evento</h2>
          <p className="text-sm text-muted-foreground">Escolha um evento para escalar atletas. Eventos encerrados ou cancelados não permitem novas escalações.</p>
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="disponivel">Disponíveis</SelectItem>
            <SelectItem value="encerrado">Encerrados</SelectItem>
            <SelectItem value="cancelado">Cancelados</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-8 text-sm">Nenhum evento encontrado.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((e) => {
            const st = computeEventLockStatus(e);
            return (
              <div key={e.id} className={`rounded-lg border p-4 space-y-2 transition ${st.locked ? "opacity-70" : "hover:border-primary hover:shadow-sm"}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold leading-tight">{e.name}</h3>
                  <Badge variant="secondary" className="text-[10px] shrink-0">{catLabel(e.event_category ?? "amistoso")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>📅 {e.start_date ? new Date(e.start_date + "T00:00:00").toLocaleDateString("pt-BR") : "sem data"}
                    {e.end_date && e.end_date !== e.start_date ? ` → ${new Date(e.end_date + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                  </p>
                  {e.location_name && <p>📍 {e.location_name}</p>}
                  <p>🏷 {e.event_categories?.length ?? 0} categoria(s)</p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {st.locked && st.reason === "encerrado" && <Badge variant="destructive" className="text-[10px]">Encerrado</Badge>}
                  {st.locked && st.reason === "cancelado" && <Badge variant="destructive" className="text-[10px]">Cancelado</Badge>}
                  {!st.locked && <Badge className="text-[10px] bg-green-600 hover:bg-green-600">Disponível</Badge>}
                </div>
                <Button
                  size="sm"
                  className="w-full"
                  variant={st.locked ? "outline" : "default"}
                  disabled={st.locked}
                  onClick={() => !st.locked && onSelect(e.id)}
                >
                  {st.locked ? "Bloqueado para escalação" : "Escalar atletas"}
                </Button>
                {st.locked && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1"
                      onClick={() => navigate({ to: "/m/criar-eventos", search: { tab: "lista" } as any })}>
                      <Pencil className="h-4 w-4 mr-1" />Editar
                    </Button>
                    <Button size="sm" variant="destructive" className="flex-1"
                      onClick={() => handleDelete(e.id, e.name)}>
                      <Trash2 className="h-4 w-4 mr-1" />Excluir
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function ConvocacaoPage() {
  const loadEvents = useServerFn(listEventsWithCategories);
  const loadStudents = useServerFn(listStudentsByCategory);
  const saveSum = useServerFn(saveSummon);
  const loadSums = useServerFn(listSummonsByEvent);
  const delSum = useServerFn(deleteSummon);
  const unsummon = useServerFn(unsummonStudent);
  const loadFees = useServerFn(listFees);
  const saveFee = useServerFn(upsertFee);
  const delFee = useServerFn(deleteFee);
  const loadPay = useServerFn(listEventPaymentStatus);
  const checkConflicts = useServerFn(checkSummonConflicts);
  const loadActivity = useServerFn(listStudentSummonActivity);
  const setChargeMethodFn = useServerFn(updateChargePaymentMethod);
  const createFeeChargesFn = useServerFn(createFeeChargesForStudents);

  const [tab, setTab] = useState("escalacao");
  const [events, setEvents] = useState<any[]>([]);

  // Escalação
  const [evId, setEvId] = useState("");
  const [catId, setCatId] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [opponent, setOpponent] = useState("Adversário");
  const [location, setLocation] = useState("");
  const [includeOthers, setIncludeOthers] = useState(false);
  // Filtro de categorias exibidas quando "incluir outras categorias" está ativo
  const [extraCatFilter, setExtraCatFilter] = useState<string>("todos");
  const [nameFilter, setNameFilter] = useState("");
  const [conflicts, setConflicts] = useState<Record<string, { name: string; reason: string }>>({});
  const [activity, setActivity] = useState<Record<string, { summons: any[]; trainings: any[] }>>({});
  const [teamSide, setTeamSide] = useState<"casa" | "visitante">("casa");
  const [myTcId, setMyTcId] = useState("");
  const [myTcName, setMyTcName] = useState("");
  const [myTcLogo, setMyTcLogo] = useState<string | null>(null);
  const [myTcPhoto, setMyTcPhoto] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState(DEFAULT_SUMMON_NOTES);

  // Consultas
  const [consultEvId, setConsultEvId] = useState("");
  const [sums, setSums] = useState<any[]>([]);
  const [eventsWithoutSummon, setEventsWithoutSummon] = useState(0);
  const [payStatus, setPayStatus] = useState<Record<string, { paid: boolean; pending: boolean; byFee: Record<string, string>; methodByFee?: Record<string, string | null> }>>({});
  const [eventFeesList, setEventFeesList] = useState<any[]>([]);
  const [consultChargeCtx, setConsultChargeCtx] = useState<{ fee: any; studentIds: string[]; info?: any } | null>(null);

  // Taxas
  const [feeType, setFeeType] = useState<FeeType>("campeonato");
  const [feeName, setFeeName] = useState("");
  const [feeAmount, setFeeAmount] = useState<string>("");
  const [feeMethod, setFeeMethod] = useState<string>("pix_comprovante");
  const [fees, setFees] = useState<any[]>([]);

  useEffect(() => { (async () => {
    const r = await loadEvents();
    setEvents(r.events ?? []);
    setMyTcId((r as any).tcId ?? "");
    setMyTcName((r as any).tcName ?? "");
    setMyTcLogo(await toMediaUrl((r as any).tcLogo ?? null));
    setMyTcPhoto((r as any).tcPhoto ?? null);
  })(); }, [loadEvents]);


  const currentEvent = useMemo(() => events.find((e: any) => e.id === evId), [events, evId]);
  const categories = currentEvent?.event_categories ?? [];
  const currentCat = categories.find((c: any) => c.id === catId);
  const internalTeams: { name: string }[] = Array.isArray(currentEvent?.internal_teams) ? currentEvent.internal_teams : [];
  const eventCat: string = currentEvent?.event_category ?? "amistoso";
  const hideOpponentForInternal = eventCat === "campeonato_interno";
  // Evento interno de dois times: o CT convidado é o próprio CT organizador
  const [externalOpen, setExternalOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const isSelfMatch = eventCat === "jogos_internos"
    || (!!myTcId && (currentEvent as any)?.invited_tc_id === myTcId);

  useEffect(() => { (async () => {
    if (!currentEvent) { setStudents([]); return; }
    const useAll = includeOthers || !currentCat?.category;
    const invited = (currentEvent as any)?.invited_name || (currentEvent as any)?.training_centers?.name || "";
    if (invited) setOpponent(invited);
    // Local sugerido: o local cadastrado na criação do evento (editável)
    const evLoc = (currentEvent as any)?.location_name || "";
    if (evLoc) setLocation(evLoc);
    const ev: any = currentEvent;
    const addr = [
      [ev?.street, ev?.number].filter(Boolean).join(", "),
      ev?.neighborhood,
      ev?.city,
    ].filter(Boolean).join(" - ");
    if (addr) setAddress(addr);
    const r = await loadStudents({ data: { category: currentCat?.category ?? "", all: useAll } });
    setStudents(r.students ?? []);
    setPicked({});
  })(); }, [currentEvent, currentCat, includeOthers, loadStudents]);

  useEffect(() => { setExtraCatFilter("todos"); }, [catId, includeOthers]);

  useEffect(() => { (async () => {
    if (!evId) { setConflicts({}); return; }
    try {
      const r = await checkConflicts({ data: {
        championship_id: evId,
        event_category_id: catId || null,
        match_date: currentCat?.match_date ?? null,
        match_time: currentCat?.start_time ?? null,
      } });
      setConflicts(r.conflicts ?? {});
    } catch { setConflicts({}); }
  })(); }, [evId, catId, currentCat, checkConflicts]);

  // Convocações ativas de cada atleta + conflito com treino no horário do jogo
  useEffect(() => { (async () => {
    if (!evId) { setActivity({}); return; }
    try {
      const r = await loadActivity({ data: {
        championship_id: evId,
        match_date: currentCat?.match_date ?? null,
        match_time: currentCat?.start_time ?? null,
      } });
      setActivity((r as any).activity ?? {});
    } catch { setActivity({}); }
  })(); }, [evId, catId, currentCat, loadActivity, students]);

  // Categorias disponíveis entre os atletas carregados (ordenadas por idade)
  const availableCats = useMemo(() => {
    const set = new Set<string>();
    for (const s of students) if (s.category) set.add(s.category);
    return [...set].sort((a, b) => (subNum(a) ?? 999) - (subNum(b) ?? 999));
  }, [students]);

  const filteredStudents = useMemo(() => {
    let list = students;
    if (includeOthers && extraCatFilter !== "todos") {
      if (extraCatFilter.startsWith("ate:")) {
        const limit = subNum(extraCatFilter.slice(4));
        if (limit != null) {
          list = list.filter((s: any) => {
            const n = subNum(s.category);
            return n != null && n <= limit;
          });
        }
      } else {
        list = list.filter((s: any) => s.category === extraCatFilter);
      }
    }
    const q = nameFilter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s: any) => (s.name ?? "").toLowerCase().includes(q));
  }, [students, nameFilter, includeOthers, extraCatFilter]);

  function isAbove(s: any): boolean {
    if (!currentCat?.category || !s.category) return false;
    return s.category !== currentCat.category;
  }

  // Consultas tab data
  useEffect(() => { (async () => {
    if (tab !== "consultas") return;
    // Count events with no summons
    let missing = 0;
    for (const ev of events) {
      if (computeEventLockStatus(ev).reason === "encerrado") continue;
      const r = await loadSums({ data: { championship_id: ev.id } });
      const cnt = (r.summons ?? []).reduce((acc: number, s: any) => acc + (s.game_summon_students?.length ?? 0), 0);
      if (cnt === 0) missing++;
    }
    setEventsWithoutSummon(missing);
  })(); }, [tab, events, loadSums]);

  useEffect(() => { (async () => {
    if (!consultEvId) { setSums([]); setPayStatus({}); setEventFeesList([]); return; }
    const r = await loadSums({ data: { championship_id: consultEvId } });
    setSums(r.summons ?? []);
    try {
      const p = await loadPay({ data: { championship_id: consultEvId } });
      setPayStatus(p.status ?? {});
      setEventFeesList(p.fees ?? []);
    } catch { setPayStatus({}); setEventFeesList([]); }
  })(); }, [consultEvId, loadSums, loadPay]);

  // Fees tab
  useEffect(() => { (async () => {
    if (tab !== "taxas") return;
    const r = await loadFees({ data: { type: feeType } });
    setFees(r.fees ?? []);
  })(); }, [tab, feeType, loadFees]);

  // Prompt para cobrar taxa após salvar escalação
  const [feePrompt, setFeePrompt] = useState<{ fees: any[]; studentIds: string[] } | null>(null);
  const [chargePromptFee, setChargePromptFee] = useState<any | null>(null);

  async function handleSaveSummon() {
    const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (!evId) return toast.error("Escolha o evento");
    if (!catId) return toast.error("Escolha a categoria do evento antes de salvar");
    if (ids.length === 0) return toast.error("Selecione ao menos um atleta");
    const blocked = ids.filter((id) => conflicts[id]);
    if (blocked.length > 0) {
      return toast.error("Remova atletas com conflito de escalação antes de salvar.");
    }
    const aboveIds = ids.filter((id) => {
      const s = students.find((x: any) => x.id === id);
      return s ? isAbove(s) : false;
    });
    try {
      await saveSum({ data: {
        championship_id: evId,
        event_category_id: catId,
        category: currentCat?.category ?? "",
        match_date: currentCat?.match_date ?? null,
        match_time: currentCat?.start_time ?? null,
        opponent: isSelfMatch
          ? `${myTcName || "Meu CT"} — Time ${teamSide === "casa" ? "1 (Mandante)" : "2 (Visitante)"}`
          : (opponent || "Adversário"),
        location: [location, address].filter((v) => v && v.trim()).join(" — "),
        student_ids: ids,
        above_ids: aboveIds,
        team_side: teamSide,
        notes: notes.trim() || null,
      }});
      toast.success("Escalação salva");
      setPicked({});
      // Se houver taxas vinculadas ao evento, oferecer envio automático da cobrança
      try {
        const f = await loadFees({ data: { championship_id: evId } });
        const linked = (f.fees ?? []).filter((x: any) => x.championship_id === evId);
        if (linked.length > 0) {
          setFeePrompt({ fees: linked, studentIds: ids });
        }
      } catch { /* silencioso */ }
    } catch (e: any) { toast.error(e?.message ?? "Erro ao salvar"); }
  }


  async function handleSaveFee() {
    if (!feeName.trim()) return toast.error("Nome obrigatório");
    try {
      await saveFee({ data: { type: feeType, name: feeName, amount: Number(feeAmount || 0), payment_method: feeMethod } });
      toast.success("Taxa criada");
      setFeeName(""); setFeeAmount("");
      const r = await loadFees({ data: { type: feeType } });
      setFees(r.fees ?? []);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  }

  async function handleDelFee(id: string) {
    if (!confirm("Excluir esta taxa?")) return;
    await delFee({ data: { id } });
    const r = await loadFees({ data: { type: feeType } });
    setFees(r.fees ?? []);
  }

  async function handleDelSummon(id: string) {
    if (!confirm("Excluir esta escalação?")) return;
    await delSum({ data: { id } });
    const r = await loadSums({ data: { championship_id: consultEvId } });
    setSums(r.summons ?? []);
  }

  async function handleUnsummon(summonId: string, studentId: string, name: string) {
    if (!confirm(`Desconvocar ${name}? A convocação e as cobranças de taxa pendentes dele neste evento serão removidas.`)) return;
    try {
      const res: any = await unsummon({ data: { summon_id: summonId, student_id: studentId } });
      const r = await loadSums({ data: { championship_id: consultEvId } });
      setSums(r.summons ?? []);
      try {
        const p = await loadPay({ data: { championship_id: consultEvId } });
        setPayStatus(p.status ?? {});
        setEventFeesList(p.fees ?? []);
      } catch { /* ignore */ }
      toast.success(
        res?.keptPaid
          ? `${name} desconvocado. ${res.removedCharges ?? 0} cobrança(s) removida(s); cobranças já pagas foram mantidas.`
          : `${name} desconvocado. ${res?.removedCharges ?? 0} cobrança(s) removida(s).`,
      );
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao desconvocar");
    }
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="rounded-md bg-primary/10 p-2"><Users className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-bold">Convocação para Jogos / Eventos</h1>
          <p className="text-sm text-muted-foreground">Gerencie escalas, consultas e taxas de eventos.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="escalacao">Escalação</TabsTrigger>
          <TabsTrigger value="consultas">Consultas e Histórico</TabsTrigger>
          <TabsTrigger value="taxas">Gerenciar Taxas</TabsTrigger>
        </TabsList>

        <TabsContent value="escalacao">
          <Card>
            <CardContent className="space-y-4 pt-6">
              {!evId ? (
                <EventPickerGrid
                  events={events}
                  onSelect={(id) => { setEvId(id); setCatId(""); setOpponent("Adversário"); }}
                  onChanged={async () => { const r = await loadEvents(); setEvents(r.events ?? []); }}
                />

              ) : (() => {
                const st = computeEventLockStatus(currentEvent);
                const catLabel = (c: string) => c === "campeonato_interno" ? "Interno"
                  : c === "jogos_internos" ? "Jogos Internos" : c === "campeonato_externo" ? "Externo" : "Amistoso";
                return (
                  <>
                    <div className="flex items-start justify-between gap-2 rounded-md border p-3 bg-muted/30">
                      <div>
                        <div className="text-xs text-muted-foreground">Evento selecionado</div>
                        <div className="font-semibold">
                          [{catLabel(currentEvent?.event_category ?? "amistoso")}] {currentEvent?.name}
                          {currentEvent?.start_date ? ` · ${new Date(currentEvent.start_date).toLocaleDateString("pt-BR")}` : ""}
                        </div>
                        <div className="mt-1 flex gap-2 flex-wrap">
                          {st.locked && st.reason === "encerrado" && <Badge variant="destructive">Evento encerrado</Badge>}
                          {st.locked && st.reason === "cancelado" && <Badge variant="destructive">Cancelado</Badge>}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => { setEvId(""); setCatId(""); setPicked({}); }}>
                        Trocar evento
                      </Button>
                    </div>

                    {st.locked && (
                      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                        <AlertCircle className="mt-0.5 h-4 w-4" />
                        <div>
                          <div className="font-semibold">
                            {st.reason === "encerrado" ? "Evento encerrado — não é possível escalar atletas." : "Este evento foi cancelado."}
                          </div>
                          <div>Consulte o histórico na aba "Consultas e Histórico".</div>
                        </div>
                      </div>
                    )}

                    {!st.locked && (
                      <>
                        <div>
                          <Label>Categoria <span className="text-destructive">*</span></Label>
                          <Select value={catId || "__none__"} onValueChange={(v) => setCatId(v === "__none__" ? "" : v)}>
                            <SelectTrigger className="bg-red-600 text-white border-red-700 font-semibold data-[placeholder]:text-white/90 [&_svg]:text-white hover:bg-red-700 focus:ring-red-500">
                              <SelectValue placeholder="-- Escolha a categoria --" />
                            </SelectTrigger>

                            <SelectContent>
                              <SelectItem disabled value="__none__">-- Escolha a categoria --</SelectItem>
                              {categories.map((c: any) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.category}
                                  {c.start_time ? ` · ${String(c.start_time).slice(0, 5)}h` : ""}
                                  {c.match_date ? ` · ${new Date(c.match_date + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                                  {c.field_name ? ` · ${c.field_name}` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!catId && (
                            <p className="text-xs text-destructive mt-1">Selecione uma categoria para salvar a escalação.</p>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge variant="secondary" className="capitalize">{eventCat.replace("_", " ")}</Badge>
                          {currentCat?.category && <Badge variant="outline">{currentCat.category}</Badge>}
                          {currentCat?.start_time && (
                            <Badge variant="outline" className="border-primary/40 text-primary">
                              Horário do jogo: {String(currentCat.start_time).slice(0, 5)}h
                              {currentCat?.end_time ? ` – ${String(currentCat.end_time).slice(0, 5)}h` : ""}
                            </Badge>
                          )}
                        </div>

                        {isSelfMatch && (
                          <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2">
                            <Label>Evento interno — convoque o Time 1 e o Time 2 separadamente</Label>
                            <Select value={teamSide} onValueChange={(v) => setTeamSide(v as "casa" | "visitante")}>
                              <SelectTrigger className="md:w-80"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="casa">Time 1 — {myTcName || "Meu CT"} (mandante)</SelectItem>
                                <SelectItem value="visitante">Time 2 — {myTcName || "Meu CT"} (visitante)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Adversário fixo: <b>{myTcName || "seu centro de treinamento"}</b> (jogos internos).
                              Salve uma escalação para cada time. O mesmo atleta não pode ficar nos dois times.
                            </p>
                            <Button
                              type="button" size="sm" variant="outline"
                              onClick={() => setExternalOpen(true)}
                            >
                              <UserPlus className="h-4 w-4 mr-2" /> Inscrever atletas externos
                            </Button>
                            <OpponentAthleteModal
                              open={externalOpen}
                              onOpenChange={setExternalOpen}
                              tcId={myTcId ?? null}
                              internal
                              event={currentEvent ? {
                                id: currentEvent.id,
                                name: currentEvent.name,
                                categories: (currentEvent.event_categories ?? []).map((c: any) => ({
                                  id: c.id, category: c.category, field_name: c.field_name,
                                })),
                              } : null}
                            />
                          </div>
                        )}
                        <div className="grid gap-4 md:grid-cols-2">
                          {!isSelfMatch && (
                            <div>
                              <Label>{hideOpponentForInternal ? "Time" : "Adversário"}</Label>
                              {hideOpponentForInternal && internalTeams.length > 0 ? (
                                <Select value={opponent} onValueChange={setOpponent}>
                                  <SelectTrigger><SelectValue placeholder="-- Escolha o time --" /></SelectTrigger>
                                  <SelectContent>
                                    {internalTeams.map((t, i) => (
                                      <SelectItem key={i} value={t.name || `Time ${i + 1}`}>{t.name || `Time ${i + 1}`}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input value={opponent} onChange={(e) => setOpponent(e.target.value)} />
                              )}
                            </div>
                          )}
                          <div><Label>Local</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Nome do ginásio/quadra" /></div>
                          <div className="md:col-span-2">
                            <Label>Endereço do local (rua, número, bairro, cidade)</Label>
                            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Ex.: Rua das Palmeiras, 250 - Centro - Belo Horizonte" />
                          </div>
                          <div className="md:col-span-2">
                            <Label>Observações gerais (lembretes) — uma por linha</Label>
                            <Textarea
                              rows={3}
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Uniforme completo&#10;Levar garrafa de água&#10;Chegar com 30 minutos de antecedência"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Aparecem no PDF da convocação como “Lembretes importantes”.</p>
                          </div>
                        </div>


                        <div className="grid gap-2 md:grid-cols-[1fr_auto] items-end">
                          <div>
                            <Label>Buscar aluno pelo nome</Label>
                            <Input value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder="Digite parte do nome…" />
                          </div>
                          <label className="flex items-center gap-2 text-sm px-2 pb-2">
                            <Checkbox checked={includeOthers} onCheckedChange={(v) => setIncludeOthers(!!v)} />
                            Incluir alunos de outras categorias
                          </label>
                        </div>
                        {includeOthers && (
                          <div className="max-w-sm">
                            <Label>Exibir categorias</Label>
                            <Select value={extraCatFilter} onValueChange={setExtraCatFilter}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="todos">Todos</SelectItem>
                                {currentCat?.category && subNum(currentCat.category) != null && (
                                  <SelectItem value={`ate:${currentCat.category}`}>
                                    Todos abaixo de {currentCat.category} (incluindo {currentCat.category})
                                  </SelectItem>
                                )}
                                {availableCats.map((cat) => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="rounded-lg border">
                          <div className="border-b bg-muted/30 px-4 py-2 text-sm font-medium">Atletas disponíveis ({filteredStudents.length})</div>
                          {filteredStudents.length === 0 ? (
                            <p className="p-4 text-sm text-muted-foreground">Nenhum atleta encontrado.</p>
                          ) : (
                            <ul className="divide-y max-h-96 overflow-y-auto">
                              {filteredStudents.map((s: any) => {
                                const above = isAbove(s);
                                const genderIcon = s.gender === "F" ? "♀️" : s.gender === "M" ? "♂️" : "";
                                const conflict = conflicts[s.id];
                                const act = activity[s.id];
                                const actSummons: any[] = act?.summons ?? [];
                                const actTrainings: any[] = act?.trainings ?? [];
                                const hasSlotConflict = actSummons.some((x) => x.same_slot) || actTrainings.length > 0;
                                return (
                                  <li key={s.id} className={`flex items-center gap-3 px-4 py-2 flex-wrap ${conflict ? "bg-destructive/10" : above ? "bg-destructive/5" : ""}`}>
                                    <Checkbox
                                      checked={!!picked[s.id]}
                                      disabled={!!conflict}
                                      onCheckedChange={(v) => setPicked((p) => ({ ...p, [s.id]: !!v }))}
                                    />
                                    <span className="flex-1 flex items-center gap-2 min-w-[180px]">
                                      {s.is_goalkeeper && <span title="Goleiro">🧤</span>}
                                      {genderIcon && <span title={s.gender === "F" ? "Feminino" : "Masculino"}>{genderIcon}</span>}
                                      {s.name}
                                      {conflict && (
                                        <span className="text-[11px] text-destructive font-medium">— {conflict.reason}</span>
                                      )}
                                    </span>
                                    {actSummons.length > 0 && (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className={`h-6 px-2 text-[10px] ${hasSlotConflict
                                              ? "border-red-400 bg-red-50 text-red-700 hover:bg-red-100"
                                              : "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
                                          >
                                            {hasSlotConflict ? "⚠ Conflito" : "📋 Convocação ativa"} ({actSummons.length})
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-80 text-xs space-y-2">
                                          <div className="font-semibold text-sm">{s.name}</div>
                                          <div className="space-y-1.5">
                                            {actSummons.map((x, i) => (
                                              <div key={i} className={`rounded border p-2 ${x.same_slot ? "border-red-300 bg-red-50" : x.same_event ? "border-amber-300 bg-amber-50" : ""}`}>
                                                <div className="font-medium">{x.event}{x.category ? ` · ${x.category}` : ""}</div>
                                                <div className="text-muted-foreground">
                                                  {x.date ? new Date(x.date + "T00:00:00").toLocaleDateString("pt-BR") : "sem data"}
                                                  {x.time ? ` às ${x.time}` : ""}
                                                  {x.team_side ? ` · ${x.team_side === "visitante" ? "Time 2" : "Time 1"}` : ""}
                                                </div>
                                                {x.opponent && <div className="text-muted-foreground">vs {x.opponent}</div>}
                                                {x.same_slot && <div className="text-red-700 font-semibold mt-1">Conflito: mesmo dia e horário</div>}
                                                {!x.same_slot && x.same_event && <div className="text-amber-700 font-semibold mt-1">Já escalado neste evento</div>}
                                              </div>
                                            ))}
                                          </div>
                                          {actTrainings.length > 0 && (
                                            <div className="rounded border border-red-300 bg-red-50 p-2">
                                              <div className="font-semibold text-red-700">Conflito com treino</div>
                                              {actTrainings.map((t, i) => (
                                                <div key={i} className="text-red-700">{t.name} {t.start}–{t.end}</div>
                                              ))}
                                            </div>
                                          )}
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                    {actSummons.length === 0 && actTrainings.length > 0 && (
                                      <Badge variant="destructive" className="text-[10px]">⚠ treino no horário</Badge>
                                    )}
                                    {s.category && (
                                      <Badge variant={above ? "destructive" : "outline"} className="text-[10px]">{s.category}</Badge>
                                    )}
                                    {above && <Badge variant="destructive" className="text-[10px]">categoria diferente</Badge>}
                                    {s.jersey_number && <Badge variant="outline">#{s.jersey_number}</Badge>}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={handleSaveSummon} disabled={!catId}><Plus className="mr-2 h-4 w-4" /> Salvar Escalação</Button>
                          <Button variant="outline" onClick={() => setWalkInOpen(true)}>
                            <UserPlus className="mr-2 h-4 w-4" /> Cadastrar atleta avulso
                          </Button>
                        </div>

                        <WalkInAthleteDialog
                          open={walkInOpen}
                          onOpenChange={setWalkInOpen}
                          defaultCategory={currentCat?.category ?? null}
                          onCreated={async (id) => {
                            const useAll = includeOthers || !currentCat?.category;
                            const r = await loadStudents({ data: { category: currentCat?.category ?? "", all: useAll } });
                            setStudents(r.students ?? []);
                            setPicked((p) => ({ ...p, [id]: true }));
                          }}
                        />

                        <EventFeesInline eventId={evId} eventName={currentEvent.name} />
                      </>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="consultas">
          <Card>
            <CardContent className="space-y-4 pt-6">
              {eventsWithoutSummon > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4" />
                  <div>
                    <div className="font-semibold">Eventos sem escalação</div>
                    <div>Existem {eventsWithoutSummon} evento(s) criados que ainda não possuem atletas escalados.</div>
                  </div>
                </div>
              )}
              <div>
                <Label>Consultar Evento</Label>
                <Select value={consultEvId} onValueChange={setConsultEvId}>
                  <SelectTrigger><SelectValue placeholder="-- Escolha um evento --" /></SelectTrigger>
                  <SelectContent>
                    {events.map((e: any) => {
                      const st = computeEventLockStatus(e);
                      return (
                        <SelectItem key={e.id} value={e.id}>
                          [{e.event_category === "campeonato_interno" ? "Interno"
                            : e.event_category === "jogos_internos" ? "Jogos Internos"
                            : e.event_category === "campeonato_externo" ? "Externo" : "Amistoso"}] {e.name}
                          {st.reason === "encerrado" ? " (encerrado)" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {consultEvId && (
                <div className="space-y-3">
                  {sums.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma escalação cadastrada para este evento.</p>
                  ) : sums.map((s: any) => {
                    const studentIds: string[] = (s.game_summon_students ?? []).map((gs: any) => gs.students?.id).filter(Boolean);
                    const unpaidIds = studentIds.filter((sid) => !payStatus[sid]?.paid && !payStatus[sid]?.pending);
                    return (
                    <Card key={s.id}>
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                          <span>{s.opponent} {s.match_date ? `· ${new Date(s.match_date).toLocaleDateString("pt-BR")}` : ""} {s.match_time ?? ""}</span>
                          {s.team_side && (
                            <Badge variant="outline" className="text-[10px]">
                              {s.team_side === "visitante" ? "Time 2 (visitante)" : "Time 1 (mandante)"}
                            </Badge>
                          )}
                          {s.event_categories?.category && (
                            <Badge variant="secondary" className="text-[10px]">{s.event_categories.category}</Badge>
                          )}
                        </CardTitle>
                        <Button variant="ghost" size="icon" onClick={() => handleDelSummon(s.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                        <CardContent className="text-sm space-y-3">
                          <div className="text-muted-foreground">{s.location}</div>
                          {(() => {
                            const sorted = [...(s.game_summon_students ?? [])]
                              .sort((a: any, b: any) => {
                                const sa = a.students, sb = b.students;
                                const rank = (x: any) => (x?.is_goalkeeper ? 0 : x?.gender === "F" ? 1 : 2);
                                const dr = rank(sa) - rank(sb);
                                if (dr !== 0) return dr;
                                return (sa?.name ?? "").localeCompare(sb?.name ?? "");
                              });
                            const hasFees = eventFeesList.length > 0;
                            let paid = 0, pending = 0, unpaid = 0;
                            for (const gs of sorted) {
                              const st = gs.students;
                              const ps = st ? payStatus[st.id] : null;
                              if (ps?.paid) paid++;
                              else if (ps?.pending) pending++;
                              else if (hasFees) unpaid++;
                            }
                            const getStatus = (st: any) => {
                              const ps = st ? payStatus[st.id] : null;
                              return ps?.paid ? "✓ PAGO" : ps?.pending ? "⏳ AGUARDANDO" : "✕ NÃO PAGO";
                            };
                            const copyList = () => {
                              const lines = [
                                `*${s.opponent}*`,
                                s.match_date ? `Data: ${new Date(s.match_date).toLocaleDateString("pt-BR")} ${s.match_time ?? ""}` : "",
                                s.location ? `Local: ${s.location}` : "",
                                "",
                                ...sorted.map((gs, i) => {
                                  const st = gs.students;
                                  const status = hasFees ? getStatus(st) : "—";
                                  return `${i + 1}. ${st?.name ?? "Atleta"}${st?.jersey_number ? ` #${st.jersey_number}` : ""} — ${status}`;
                                }),
                              ].filter(Boolean);
                              const text = lines.join("\n");
                              if (navigator.clipboard?.writeText) {
                                navigator.clipboard.writeText(text).then(() => toast.success("Lista copiada para colar no WhatsApp"));
                              } else {
                                 // Fallback sem manipular o DOM da página (evita
                                 // conflito com o React ao remover nós do body).
                                 const area = document.createElement("textarea");
                                 area.value = text;
                                 area.style.position = "fixed";
                                 area.style.opacity = "0";
                                 document.documentElement.appendChild(area);
                                 area.select();
                                 document.execCommand("copy");
                                 area.remove();

                                toast.success("Lista copiada para colar no WhatsApp");
                              }
                            };
                            return (
                              <>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
                                  <div className="flex flex-wrap items-center gap-2 text-xs">
                                    <span className="font-medium">Resumo:</span>
                                    {hasFees ? (
                                      <>
                                        <span className="inline-flex items-center rounded-full border border-green-300 bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800">✓ Pago {paid}</span>
                                        <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">⏳ Aguardando {pending}</span>
                                        <span className="inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-800">✕ Não pago {unpaid}</span>
                                      </>
                                    ) : (
                                      <span className="text-muted-foreground">{sorted.length} atleta(s) escalado(s)</span>
                                    )}
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700" onClick={() => {
                                      if (studentIds.length === 0) { toast.error("Nenhum atleta escalado."); return; }
                                      setConsultChargeCtx({
                                        fee: eventFeesList[0]
                                          ? { ...eventFeesList[0], championship_id: consultEvId }
                                          : { id: s.id, name: "Convocação", type: "campeonato", amount: 0, championship_id: consultEvId, payment_method: null },
                                        studentIds,
                                        info: summonEventInfo(s, events.find((e: any) => e.id === consultEvId)),
                                      });
                                    }}>
                                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> Enviar WhatsApp p/ todos ({studentIds.length})
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7" onClick={copyList}>
                                      <Clipboard className="h-3.5 w-3.5 mr-1" /> Copiar lista
                                    </Button>

                                    <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                                      try {
                                        const ev: any = events.find((x: any) => x.id === consultEvId) ?? {};
                                        const doc = await buildSummonPdf({
                                          tcName: myTcName || "Centro de Treinamento",
                                          tcLogoUrl: myTcLogo,
                                          tcPhotoUrl: myTcPhoto,
                                          eventName: ev.name ?? "Evento",
                                          eventDate: s.match_date
                                            ? new Date(s.match_date + "T00:00:00").toLocaleDateString("pt-BR")
                                            : (ev.start_date ? new Date(ev.start_date + "T00:00:00").toLocaleDateString("pt-BR") : null),
                                          eventTime: s.match_time ? String(s.match_time).slice(0, 5) : null,
                                          address: s.location || [[ev.street, ev.number].filter(Boolean).join(", "), ev.neighborhood, ev.city].filter(Boolean).join(" - "),
                                          category: s.event_categories?.category ?? null,
                                          fieldName: s.event_categories?.field_name ?? null,
                                          opponent: s.opponent ?? null,
                                          teamSide: s.team_side === "visitante" ? "Time 2 (visitante)" : s.team_side === "casa" ? "Time 1 (mandante)" : null,
                                          notes: s.notes ?? DEFAULT_SUMMON_NOTES,
                                          players: sorted.map((gs: any) => {
                                            const st2 = gs.students;
                                            const ps = st2 ? payStatus[st2.id] : null;
                                            return {
                                              name: st2?.name ?? "Atleta",
                                              jersey_number: st2?.jersey_number ?? null,
                                              category: st2?.category ?? null,
                                              is_goalkeeper: !!st2?.is_goalkeeper,
                                              payment: !hasFees ? "sem_taxa" : ps?.paid ? "pago" : ps?.pending ? "aguardando" : "nao_pago",
                                            } as const;
                                          }),
                                        });
                                        doc.save(`convocacao-${(ev.name ?? "evento").replace(/[^\w]+/g, "-").toLowerCase()}.pdf`);
                                      } catch (err: any) {
                                        toast.error(err?.message ?? "Erro ao gerar PDF");
                                      }
                                    }}>
                                      <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex flex-col divide-y rounded-md border">
                                  {sorted.map((gs: any, i: number) => {
                                    const st = gs.students;
                                    const genderIcon = st?.gender === "F" ? "♀️" : st?.gender === "M" ? "♂️" : "";
                                    const ps = st ? payStatus[st.id] : null;
                                    const pillClass = ps?.paid
                                      ? "bg-green-100 text-green-800 border-green-300"
                                      : ps?.pending
                                      ? "bg-amber-100 text-amber-800 border-amber-300"
                                      : "bg-red-100 text-red-800 border-red-300";
                                    const pillLabel = ps?.paid ? "✓ PAGO" : ps?.pending ? "⏳ AGUARDANDO" : "✕ NÃO PAGO";
                                    return (
                                      <div key={gs.id} className="flex items-center justify-between gap-2 px-3 py-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="w-5 shrink-0 text-muted-foreground text-xs">{i + 1}.</span>
                                          <div className={`min-w-0 truncate text-sm ${gs.is_above_category ? "text-red-600 font-medium" : ""}`}>
                                            {st?.is_goalkeeper ? "🧤 " : ""}{genderIcon ? `${genderIcon} ` : ""}{st?.name}{st?.jersey_number ? ` #${st.jersey_number}` : ""}
                                            {gs.is_above_category && st?.category ? ` (${st.category})` : ""}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                          {hasFees && (
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${pillClass}`}>
                                              {pillLabel}
                                            </span>
                                          )}
                                          {hasFees && eventFeesList.length > 0 && !ps?.paid && (
                                            <Select
                                              value={ps?.methodByFee?.[eventFeesList[0].id] ?? eventFeesList[0].payment_method ?? "pix_comprovante"}
                                              onValueChange={async (v) => {
                                                try {
                                                  const feeId = eventFeesList[0].id;
                                                  const res: any = await setChargeMethodFn({ data: { fee_id: feeId, student_id: st.id, payment_method: v } });
                                                  if (res?.notFound) {
                                                    await createFeeChargesFn({ data: { fee_id: feeId, student_ids: [st.id] } });
                                                    await setChargeMethodFn({ data: { fee_id: feeId, student_id: st.id, payment_method: v } });
                                                  }
                                                  const p2 = await loadPay({ data: { championship_id: consultEvId! } });
                                                  setPayStatus(p2.status ?? {});
                                                  toast.success(`Forma de pagamento: ${paymentMethodLabel(v)}`);
                                                } catch (e: any) {
                                                  toast.error(e?.message || "Erro ao alterar a forma de pagamento");
                                                }
                                              }}
                                            >
                                              <SelectTrigger className="h-7 w-[190px] text-[11px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                {PAYMENT_METHODS.map((pm) => (
                                                  <SelectItem key={pm.value} value={pm.value} className="text-xs">{pm.label}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          )}
                                          {hasFees && eventFeesList.length > 0 && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 px-2"
                                              disabled={!!ps?.paid}
                                              title={ps?.paid ? "Taxa já paga — cobrança desabilitada" : "Enviar cobrança por WhatsApp"}
                                              onClick={() => setConsultChargeCtx({ fee: { ...eventFeesList[0], championship_id: consultEvId }, studentIds: [st.id], info: summonEventInfo(s, events.find((e: any) => e.id === consultEvId)) })}
                                            >
                                              <MessageCircle className="h-3.5 w-3.5 mr-1" /> Cobrar
                                            </Button>
                                          )}
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-destructive hover:text-destructive"
                                            title="Desconvocar atleta"
                                            onClick={() => handleUnsummon(s.id, st.id, st?.name ?? "Atleta")}
                                          >
                                            <UserMinus className="h-3.5 w-3.5 mr-1" /> Desconvocar
                                          </Button>
                                        </div>

                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                          {eventFeesList.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                              <span className="text-xs text-muted-foreground self-center">Cobrar não pagos:</span>
                              {eventFeesList.map((f: any) => (
                                <Button
                                  key={f.id}
                                  size="sm"
                                  variant="outline"
                                  disabled={unpaidIds.length === 0}
                                  onClick={() => setConsultChargeCtx({ fee: { ...f, championship_id: consultEvId }, studentIds: unpaidIds, info: summonEventInfo(s, events.find((e: any) => e.id === consultEvId)) })}
                                >
                                  <MessageCircle className="h-3.5 w-3.5 mr-1" />
                                  {f.name} ({unpaidIds.length})
                                </Button>
                              ))}
                            </div>
                          )}
                        </CardContent>
                    </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="taxas">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex gap-2">
                {(["campeonato","arbitragem","material"] as FeeType[]).map((t) => (
                  <Button key={t} variant={feeType === t ? "default" : "outline"} onClick={() => setFeeType(t)} className="capitalize">{t}</Button>
                ))}
              </div>
              <div className="grid items-end gap-3 md:grid-cols-[1fr_160px_220px_auto]">
                <div><Label>Nome da Taxa</Label><Input placeholder="Ex: Taxa Copinha 2026" value={feeName} onChange={(e) => setFeeName(e.target.value)} /></div>
                <div><Label>Valor (R$)</Label><Input type="number" step="0.01" placeholder="50.00" value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} /></div>
                <div>
                  <Label>Forma de pagamento</Label>
                  <Select value={feeMethod} onValueChange={setFeeMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((pm) => (
                        <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSaveFee}><Plus className="mr-2 h-4 w-4" /> Criar Nova Taxa</Button>
              </div>
              <div className="rounded-md border">
                <div className="grid grid-cols-[1fr_160px_140px_80px] gap-2 border-b bg-muted/30 px-4 py-2 text-sm font-medium">
                  <span>Nome</span><span>Forma pagto</span><span>Valor</span><span className="text-right">Ações</span>
                </div>
                {fees.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">Nenhuma taxa cadastrada.</p>
                ) : fees.map((f: any) => (
                  <div key={f.id} className="grid grid-cols-[1fr_160px_140px_80px] items-center gap-2 border-b px-4 py-2 text-sm last:border-0">
                    <div>
                      <div>{f.name}</div>
                      {f.championships?.name && <div className="text-xs text-muted-foreground">Vinculada a: {f.championships.name}</div>}
                    </div>
                    <div className="text-xs">{f.payment_method ? paymentMethodLabel(f.payment_method) : <span className="text-muted-foreground">—</span>}</div>
                    <div>R$ {Number(f.amount).toFixed(2)}</div>
                    <div className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleDelFee(f.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Prompt para cobrar taxa após salvar escalação */}
      <Dialog open={!!feePrompt} onOpenChange={(o) => !o && setFeePrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" />Cobrar taxa dos convocados?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Este evento possui {feePrompt?.fees.length ?? 0} taxa(s) vinculada(s). Envie a cobrança via WhatsApp para os {feePrompt?.studentIds.length ?? 0} atleta(s) que você acabou de escalar.
          </p>
          <div className="space-y-2 mt-2">
            {(feePrompt?.fees ?? []).map((f: any) => (
              <div key={f.id} className="flex items-center justify-between rounded border p-2 text-sm">
                <div>
                  <div className="font-medium">{f.name} <Badge variant="outline" className="ml-1 capitalize text-[10px]">{f.type}</Badge></div>
                  <div className="text-xs text-muted-foreground">R$ {Number(f.amount).toFixed(2)}</div>
                </div>
                <Button size="sm" onClick={() => { setChargePromptFee(f); setFeePrompt(null); }}>
                  <MessageCircle className="h-4 w-4 mr-1" />Cobrar
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeePrompt(null)}>Agora não</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FeeChargeWhatsappModal
        fee={chargePromptFee}
        eventName={events.find((e: any) => e.id === chargePromptFee?.championship_id)?.name}
        eventInfo={(() => {
          const ev = events.find((e: any) => e.id === chargePromptFee?.championship_id);
          return ev ? { address: eventAddress(ev), date: ev.start_date ? new Date(ev.start_date + "T00:00:00").toLocaleDateString("pt-BR") : null } : null;
        })()}
        open={!!chargePromptFee}
        onOpenChange={(v) => !v && setChargePromptFee(null)}
        restrictStudentIds={feePrompt?.studentIds ?? null}
      />

      <FeeChargeWhatsappModal
        fee={consultChargeCtx?.fee ?? null}
        eventName={events.find((e: any) => e.id === consultEvId)?.name}
        eventInfo={consultChargeCtx?.info ?? null}
        open={!!consultChargeCtx}
        onOpenChange={(v) => { if (!v) { setConsultChargeCtx(null); if (consultEvId) loadPay({ data: { championship_id: consultEvId } }).then((p) => { setPayStatus(p.status ?? {}); setEventFeesList(p.fees ?? []); }).catch(() => {}); } }}
        restrictStudentIds={consultChargeCtx?.studentIds ?? null}
      />
    </div>
  );
}

function EventFeesInline({ eventId, eventName }: { eventId: string; eventName: string }) {
  const loadFn = useServerFn(listFees);
  const saveFn = useServerFn(upsertFee);
  const delFn = useServerFn(deleteFee);
  const linkFn = useServerFn(linkFeeToEvent);

  const [type, setType] = useState<"campeonato" | "arbitragem" | "material">("campeonato");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<string>("pix_comprovante");
  const [eventFees, setEventFees] = useState<any[]>([]);
  const [available, setAvailable] = useState<any[]>([]);
  const [linkId, setLinkId] = useState("");
  const [chargeFee, setChargeFee] = useState<any | null>(null);

  const reload = async () => {
    if (!eventId) return;
    const linked = await loadFn({ data: { championship_id: eventId } });
    setEventFees(linked.fees ?? []);
    const all = await loadFn({ data: { type } });
    setAvailable((all.fees ?? []).filter((f: any) => !f.championship_id));
  };
  useEffect(() => { reload().catch(() => {}); /* eslint-disable-next-line */ }, [eventId, type]);

  if (!eventId) return null;

  const create = async () => {
    if (!name.trim()) return toast.error("Nome obrigatório");
    await saveFn({ data: { type, name, amount: Number(amount || 0), championship_id: eventId, payment_method: method } });
    setName(""); setAmount("");
    toast.success("Taxa criada e vinculada");
    reload();
  };
  const link = async () => {
    if (!linkId) return;
    await linkFn({ data: { fee_id: linkId, championship_id: eventId } });
    setLinkId("");
    toast.success("Taxa vinculada aos convocados");
    reload();
  };
  const unlink = async (id: string) => {
    await linkFn({ data: { fee_id: id, championship_id: null } });
    reload();
  };
  const remove = async (id: string) => {
    if (!confirm("Excluir taxa?")) return;
    await delFn({ data: { id } });
    reload();
  };

  return (
    <Card className="mt-2 border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary" /> Taxas deste evento
        </CardTitle>
        <p className="text-xs text-muted-foreground">Vincule uma taxa e cobre os alunos escalados por WhatsApp.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 md:grid-cols-[140px_1fr_120px_180px_auto]">
          <Select value={type} onValueChange={(v) => setType(v as any)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="campeonato">Campeonato</SelectItem>
              <SelectItem value="arbitragem">Arbitragem</SelectItem>
              <SelectItem value="material">Material</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Nome da nova taxa" value={name} onChange={(e) => setName(e.target.value)} />
          <Input type="number" step="0.01" placeholder="Valor" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger><SelectValue placeholder="Forma de pagamento" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((pm) => (
                <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={create}><Plus className="h-4 w-4 mr-1" />Criar e vincular</Button>
        </div>

        <div className="flex gap-2">
          <Select value={linkId} onValueChange={setLinkId}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="-- Vincular taxa avulsa existente --" /></SelectTrigger>
            <SelectContent>
              {available.length === 0 && <SelectItem disabled value="none">Nenhuma taxa avulsa</SelectItem>}
              {available.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} (R$ {Number(f.amount).toFixed(2)})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={link} disabled={!linkId} variant="outline">Vincular</Button>
        </div>

        <div>
          <div className="mb-1 text-sm font-semibold">Vinculadas</div>
          {eventFees.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma taxa vinculada.</p>
          ) : (
            <div className="space-y-2">
              {eventFees.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{f.name} <Badge variant="outline" className="ml-1 capitalize">{f.type}</Badge></div>
                    <div className="text-xs text-muted-foreground">R$ {Number(f.amount).toFixed(2)}{f.payment_method ? ` · ${paymentMethodLabel(f.payment_method)}` : ""}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => setChargeFee({ ...f, championship_id: eventId })}>
                      <MessageCircle className="h-4 w-4 mr-1" />Cobrar convocados
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => unlink(f.id)}>Desvincular</Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(f.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
      <FeeChargeWhatsappModal
        fee={chargeFee}
        eventName={eventName}
        open={!!chargeFee}
        onOpenChange={(v) => !v && setChargeFee(null)}
      />
    </Card>
  );
}

void Trophy;
